import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { onboardingConfig } from '../../onboarding/onboardingConfig';
import OnboardingCompletionModal from './OnboardingCompletionModal';
import Phase2OnboardingModal from './Phase2OnboardingModal';
import Phase2CompletionModal from './Phase2CompletionModal';
import UnifiedOnboardingWidget from './UnifiedOnboardingWidget';

const OnboardingChecklist = ({ onStepClick, completedSteps = [] }) => {
  const { user, updateOnboardingStatus, updateCurrentOnboardingPhase, setPhase1CompletionCelebrated } = useAuth();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showPhase1CompletionModal, setShowPhase1CompletionModal] = useState(false);
  const [showPhase2Modal, setShowPhase2Modal] = useState(false);
  const [showPhase2CompletionModal, setShowPhase2CompletionModal] = useState(false);
  const [hasShownPhase2CompletionModal, setHasShownPhase2CompletionModal] = useState(false);
  const [isTransitioningToPhase2, setIsTransitioningToPhase2] = useState(false);

  // Get current phase from user object, default to 'phase_1'
  const currentPhase = user?.currentOnboardingPhase || 'phase_1';

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Phase 1 completion detection (5/5 steps)
  useEffect(() => {
    // Only run if onboarding is in progress
    if (!user || user.onboardingStatus !== 'in_progress') {
      return;
    }

    // Don't show modal if we're transitioning to Phase 2
    if (isTransitioningToPhase2) {
      return;
    }

    if (currentPhase === 'phase_1') {
      const phase1Steps = onboardingConfig.phase1.checklist;
      const phase1Completed = phase1Steps.filter(step => completedSteps.includes(step.id)).length;
      const phase1Complete = phase1Completed === phase1Steps.length;

      // Only show modal if all Phase 1 steps are complete AND we haven't celebrated yet
      if (phase1Complete && !user?.phase1_completion_celebrated) {
        setShowPhase1CompletionModal(true);
      }
    }
  }, [completedSteps, currentPhase, user?.phase1_completion_celebrated, user?.onboardingStatus, isTransitioningToPhase2, user]);

  // Reset transition flag after Phase 2 modal is opened
  useEffect(() => {
    if (showPhase2Modal && isTransitioningToPhase2) {
      setIsTransitioningToPhase2(false);
    }
  }, [showPhase2Modal, isTransitioningToPhase2]);

  // Phase 2 completion detection (5/5 steps)
  useEffect(() => {
    // Only run if onboarding is in progress
    if (!user || user.onboardingStatus !== 'in_progress') {
      return;
    }

    if (currentPhase === 'phase_2') {
      const phase2Steps = onboardingConfig.phase2.checklist;
      const phase2Completed = phase2Steps.filter(step => completedSteps.includes(step.id)).length;
      const phase2Complete = phase2Completed === phase2Steps.length;

      if (phase2Complete && !hasShownPhase2CompletionModal) {
        setShowPhase2CompletionModal(true);
        setHasShownPhase2CompletionModal(true);
      }
    }
  }, [completedSteps, hasShownPhase2CompletionModal, currentPhase, user?.onboardingStatus, user]);

  if (!user || user.onboardingStatus !== 'in_progress') {
    return null;
  }

  // Define public paths where onboarding should NOT be visible
  const publicPaths = [
    '/quotes/public',
    '/invoices/public',
    '/book',
    '/blog',
    '/estimate-software',
    '/privacy-policy',
    '/terms-of-service',
    '/login',
    '/register',
    '/forgot-password',
    '/reset-password'
  ];

  // Check if current path starts with any of the public paths
  const isPublicPage = publicPaths.some(path => location.pathname.startsWith(path));

  if (isPublicPage) {
    return null;
  }

  const handleStartPhase2 = async () => {
    // Set transition flag to prevent Phase 1 modal from re-opening
    setIsTransitioningToPhase2(true);
    // Immediately close the Phase 1 modal
    setShowPhase1CompletionModal(false);

    try {
      // Mark Phase 1 celebration as shown so confetti doesn't trigger on reload
      await setPhase1CompletionCelebrated();
      // Now transition to Phase 2
      await updateCurrentOnboardingPhase('phase_2');
      // Show Phase 2 modal after phase transition
      setShowPhase2Modal(true);
    } catch (error) {
      console.error('Error transitioning to Phase 2:', error);
      // If something fails, allow Phase 1 modal to show again
      setIsTransitioningToPhase2(false);
      setShowPhase1CompletionModal(true);
    }
  };

  const handleCompleteOnboarding = async () => {
    setShowPhase2CompletionModal(false);
    await updateOnboardingStatus('completed');
    await updateCurrentOnboardingPhase('completed');
  };

  return (
    <>
      {/* Phase 1 Completion Modal */}
      <OnboardingCompletionModal
        isOpen={showPhase1CompletionModal}
        onClose={() => setShowPhase1CompletionModal(false)}
        onStartPhase2={handleStartPhase2}
      />

      {/* Phase 2 Introduction Modal */}
      <Phase2OnboardingModal
        isOpen={showPhase2Modal && !showPhase2CompletionModal}
        onClose={() => setShowPhase2Modal(false)}
        onStepClick={(tourName) => {
          setShowPhase2Modal(false);
          onStepClick(tourName);
        }}
        completedSteps={completedSteps}
      />

      {/* Phase 2 Completion Modal */}
      <Phase2CompletionModal
        isOpen={showPhase2CompletionModal}
        onClose={() => setShowPhase2CompletionModal(false)}
        onCompleteOnboarding={handleCompleteOnboarding}
      />

      {/* Unified Onboarding Widget - Shows for both Phase 1 and Phase 2 */}
      <UnifiedOnboardingWidget
        currentPhase={currentPhase}
        completedSteps={completedSteps}
        onStepClick={onStepClick}
        isMobile={isMobile}
      />
    </>
  );
};

export default OnboardingChecklist;
